<template>
  <div
    :id="id"
    class="route-builder-table-expanded-details"
    :class="{
      'three-col': !isShipment,
      'four-col': isShipment,
    }"
    tabindex="-1"
  >
    <template
      v-if="
        // VSCode's syntax higlighting gets borked with instanceof in templates
        entity instanceof ShipOrder
      "
    >
      <RouteBuilderTableExpandedDetailsRow
        :entity="entity"
        :shipping-point="entity.origin"
        :proposed-shipments="proposedShipments"
        :mileage="mileage"
        :readonly="readonly"
        has-next-stop
        @add-stop="emit('add-stop', $event)"
        @remove-ship-order="emit('remove-ship-order', $event)"
        @remove-stop="emit('remove-stop', $event)"
      />

      <RouteBuilderTableIntermediateShipmentRow
        v-if="!!intermediateShipmentsForShipOrder.length"
        :intermediate-shipments="intermediateShipmentsForShipOrder"
      />

      <RouteBuilderTableExpandedDetailsRow
        :entity="entity"
        :shipping-point="entity.destination"
        :proposed-shipments="proposedShipments"
        :mileage="mileage"
        :readonly="readonly"
        @add-stop="emit('add-stop', $event)"
        @remove-ship-order="emit('remove-ship-order', $event)"
        @remove-stop="emit('remove-stop', $event)"
      />
    </template>
    <template v-else>
      <RouteBuilderTableExpandedDetailsRow
        v-for="stop in entity.stops"
        :key="stop.sequenceNumber"
        :entity="entity"
        :shipping-point="stop"
        :proposed-shipments="proposedShipments"
        :mileage="mileage"
        :readonly="readonly"
        :has-next-stop="stop.sequenceNumber < entity.stops.length"
        @add-stop="emit('add-stop', $event)"
        @edit-appointment-start="onEditAppointmentStart(stop, $event)"
        @edit-appointment-end="onEditAppointmentEnd(stop, $event)"
        @edit-appointment-time-zone="onEditAppointmentTimeZone(stop, $event)"
        @remove-ship-order="emit('remove-ship-order', $event)"
        @remove-stop="emit('remove-stop', $event)"
      />
    </template>

    <div
      v-if="shipmentIdentifier"
      class="route-builder-table-expanded-details-bottom-row"
    >
      <div class="cell stop-column">
        <RygenButton
          v-if="!readonly && entity.stops.length > 2"
          mode="tertiary"
          :icon="PrimeIcons.SORT_ALT"
          :label="t('multistop.rearrangeStops')"
          @click="emit('rearrange-stops', { shipmentIdentifier })"
        />
        <RygenButton
          v-if="!readonly"
          mode="tertiary"
          :icon="PrimeIcons.PLUS"
          :label="t('common.addParam', { param: t('shipments.stops') })"
          @click="onAddStop"
        />
      </div>

      <div class="cell appointment-column">&nbsp;</div>

      <div class="cell line-items-column">&nbsp;</div>

      <div class="cell ship-orders-column">
        <!-- TODO: Reenable this after we determine how to generate the cost estimate -->
        <InfoBox v-if="false" :items="costEstimateInfoItem" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { useFlag } from '@unleash/proxy-client-vue';
import axios from 'axios';
import { DateTime } from 'luxon';
import type { PropType, Ref } from 'vue';
import { computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { renderMoney } from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { ShipOrder, StopMileageResponse } from '@/model';
import {
  RequestAddStopToShipmentPayload,
  ProposedShipment,
  RearrangeStopsPayload,
  RemoveShipOrderFromShipmentPayload,
  RemoveStopFromShipmentPayload,
  CostEstimateResponse,
  UpdateAppointmentPayload,
  StopSelector,
} from '@/model/routeBuilder';
import {
  RoutePlanProposedShipment,
  RoutePlanStop,
} from '@/model/routeBuilder/phase2';

import RouteBuilderTableExpandedDetailsRow from '@/components/routeBuilder/RouteBuilderTableExpandedDetailsRow.vue';
import RouteBuilderTableIntermediateShipmentRow from '@/components/routeBuilder/RouteBuilderTableIntermediateShipmentRow.vue';
import InfoBox from '@/components/ui/InfoBox.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';

const props = defineProps({
  entity: {
    type: Object as PropType<
      ShipOrder | ProposedShipment | RoutePlanProposedShipment
    >,
    default: () => new ShipOrder(),
  },
  proposedShipments: {
    type: Array as PropType<ProposedShipment[] | RoutePlanProposedShipment[]>,
    default: () => [],
  },
  mileage: {
    type: Object as PropType<Loadable<StopMileageResponse[]>>,
    default: () => ({ loading: false }),
  },
  readonly: {
    type: Boolean,
    default: () => false,
  },
});
const { entity, proposedShipments, mileage, readonly } = toRefs(props);
const emit = defineEmits<{
  (event: 'add-stop', payload: RequestAddStopToShipmentPayload): void;
  (event: 'rearrange-stops', payload: RearrangeStopsPayload): void;
  (
    event: 'remove-ship-order',
    payload: RemoveShipOrderFromShipmentPayload,
  ): void;
  (event: 'remove-stop', payload: RemoveStopFromShipmentPayload): void;
  (event: 'update-appointment', payload: UpdateAppointmentPayload): void;
}>();

const isTransportSegmentEnabled = useFlag(
  'route-builder-transport-segment-feat-flag',
);

const { t } = useI18n();

const costEstimate: Ref<Loadable<CostEstimateResponse>> = ref({
  loading: false,
});

const isShipment = computed(() => {
  return (
    entity.value instanceof ProposedShipment ||
    entity.value instanceof RoutePlanProposedShipment
  );
});

const id = computed(() => {
  return entity.value instanceof ShipOrder
    ? `ship-order-expanded-detail-${entity.value.id}`
    : `shipment-expanded-detail-${entity.value.identifier}`;
});

const shipmentIdentifier = computed(() => {
  return entity.value instanceof ShipOrder
    ? undefined
    : entity.value.identifier;
});

const costEstimateInfoItem = computed(() => [
  {
    id: 'estimate',
    label: t('common.estimate'),
    value: renderMoney(costEstimate.value.data?.lowestCost),
    loading: costEstimate.value.loading,
  },
]);

const shipmentsContainingShipOrder = computed(() => {
  const shipOrderOrShipment = entity.value;
  return shipOrderOrShipment instanceof ShipOrder
    ? proposedShipments.value.filter((s) => s.hasShipOrder(shipOrderOrShipment))
    : undefined;
});

const intermediateShipmentsForShipOrder = computed(() => {
  if (!isTransportSegmentEnabled.value) {
    return [];
  }

  if (!(entity.value instanceof ShipOrder)) {
    return [];
  }

  const so = entity.value;
  // isTransportSegmentEnabled guard guarantees we're in phase 2
  const shipments =
    (shipmentsContainingShipOrder.value as RoutePlanProposedShipment[]) ?? [];
  return shipments.filter((s) => {
    const hasOrigin = s.hasShipOrderShippingPoint(so.origin, so);
    const hasDestination = s.hasShipOrderShippingPoint(so.destination, so);
    return !hasOrigin && !hasDestination;
  });
});

const findCostEstimate = async (
  proposedShipment: ProposedShipment | RoutePlanProposedShipment,
) => {
  try {
    // single item array should be fine but typescript thinks its
    // Array<ProposedShipment | RoutePlanProposedShipment>
    const payload = [proposedShipment] as
      ProposedShipment[] | RoutePlanProposedShipment[];
    costEstimate.value.loading = true;
    costEstimate.value.data =
      await api.shipmentPlanning.findCostEstimate(payload);
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error fetching cost estimate', e);
    costEstimate.value.error = e;
  } finally {
    costEstimate.value.loading = false;
  }
};

const emitUpdateAppointment = (
  shipment: RoutePlanProposedShipment,
  stop: RoutePlanStop,
  appointment: UpdateAppointmentPayload['appointment'],
) => {
  emit('update-appointment', {
    shipmentIdentifier: shipment.identifier,
    stopSequenceNumber: stop.sequenceNumber,
    appointment,
  });
};

const onEditAppointmentStart = (stop: RoutePlanStop, newVal?: DateTime) => {
  const appointment = {
    start: newVal,
    end: stop.requestedAppointmentLatest,
    timeZone: stop.location.timeZone,
  };

  emitUpdateAppointment(
    entity.value as RoutePlanProposedShipment,
    stop,
    appointment,
  );
};

const onEditAppointmentEnd = (stop: RoutePlanStop, newVal?: DateTime) => {
  const appointment = {
    start: stop.requestedAppointmentEarliest,
    end: newVal,
    timeZone: stop.location.timeZone,
  };

  emitUpdateAppointment(
    entity.value as RoutePlanProposedShipment,
    stop,
    appointment,
  );
};

const onEditAppointmentTimeZone = (stop: RoutePlanStop, newVal?: string) => {
  const appointment = {
    start: stop.requestedAppointmentEarliest,
    end: stop.requestedAppointmentLatest,
    timeZone: newVal,
  };

  emitUpdateAppointment(
    entity.value as RoutePlanProposedShipment,
    stop,
    appointment,
  );
};

const onAddStop = () => {
  if (!shipmentIdentifier.value) {
    throw new Error(
      'Cannot add stop to a proposed shipment without a shipment identifier',
    );
  }

  if (!isTransportSegmentEnabled.value) {
    // Phase 1, always add a brand new stop
    emit('add-stop', {
      shipmentIdentifier: shipmentIdentifier.value,
    });
    return;
  }

  // Phase 2, if there's a stop with a missing location, then the user has to
  // choose a location for that stop first before adding a new stop
  const shipment = entity.value as RoutePlanProposedShipment;
  const stopWithMissingLocation = shipment.stops.find(
    (s) => s.isPlaceholderStop,
  );

  if (stopWithMissingLocation) {
    let stopSelector: StopSelector;
    if (stopWithMissingLocation.sequenceNumber === 1) {
      stopSelector = 'origin';
    } else if (
      stopWithMissingLocation.sequenceNumber === shipment.stops.length
    ) {
      stopSelector = 'destination';
    } else {
      stopSelector = 'intermediate';
    }

    emit('add-stop', {
      stopType: stopWithMissingLocation.stopType,
      stopSelector,
      sequenceNumber: stopWithMissingLocation.sequenceNumber,
      shipmentIdentifier: stopWithMissingLocation.shipmentIdentifier,
    });
    return;
  }

  // Phase 2, no stops are missing locations, so the user can add a new stop
  emit('add-stop', {
    shipmentIdentifier: shipmentIdentifier.value,
  });
};

watch(
  entity,
  (newVal) => {
    if (newVal instanceof ProposedShipment) {
      findCostEstimate(newVal);
    }
  },
  { immediate: true },
);
</script>

<style>
.route-builder-table-expanded-details {
  --info-box-color: var(--p-green-500);
  --info-box-background-color: var(--p-green-50);

  display: grid;
  grid-auto-rows: min-content;

  &.three-col {
    /* 4 columns; first columns takes as much as it needs, other 2 split remaining space evenly */
    grid-template-columns: auto 1fr 1fr;
    padding-block: var(--sp-6);
  }

  &.four-col {
    /* 4 columns; first columns takes as much as it needs, other 3 split remaining space evenly */
    grid-template-columns: auto 1fr 1fr 1fr;
    padding-block: var(--sp-6);
  }

  .route-builder-table-expanded-details-bottom-row {
    display: contents;

    .cell.stop-column {
      display: flex;
      justify-content: flex-end;
      gap: var(--gap-1);
      padding-inline: 0 var(--sp-6);
      margin-inline: 0 var(--sp-6);
    }

    .cell.ship-orders-column {
      display: flex;
      justify-content: flex-end;

      .info-box {
        flex: 0 1 50%;
      }
    }
  }

  /* TODO: container query for smaller screens where cells wont fit side-by-side */
}
</style>
